import styled, { css } from 'styled-components';
import { Col, Collapse } from 'antd';
import { BurgerIcon } from '../common/Burger/BurgerIcon';
import { LAYOUT, media } from '../../styles/themes/constants';

export const HeaderActionWrapper = styled.div`
  cursor: pointer;

  & > .ant-btn > span[role='img'],
  .ant-badge {
    font-size: 1.25rem;

    @media only screen and ${media.md} {
      font-size: 1.625rem;
    }
  }

  & .ant-badge {
    display: inline-block;
  }
`;

export const DropdownCollapse = styled(Collapse)`
  & > .ant-collapse-item > .ant-collapse-header {
    font-weight: 600;
    font-size: 0.875rem;

    color: var(--primary-color);

    @media only screen and ${media.md} {
      font-size: 1rem;
    }
  }

  & > .ant-collapse-item-disabled > .ant-collapse-header {
    cursor: default;

    & > span[role='img'] {
      display: none;
    }
  }
`;

export const BurgerCol = styled(Col)`
  z-index: 999;
  display: flex;
`;

export const MobileBurger = styled(BurgerIcon)`
  width: 1.75rem;
  height: 1.75rem;
  margin-right: -0.5rem;
  color: var(--text-main-color);

  ${(props) =>
    props.isCross &&
    css`
      color: var(--text-secondary-color);
    `};
`;

export const SearchColumn = styled(Col)`
  padding: ${LAYOUT.desktop.paddingVertical} ${LAYOUT.desktop.paddingHorizontal};
`;

interface ProfileColumn {
  $isTwoColumnsLayout: boolean;
}

export const ProfileColumn = styled(Col)<ProfileColumn>`
  @media only screen and ${media.md} {
    ${(props) =>
      props?.$isTwoColumnsLayout &&
      css`
        background-color: var(--sider-background-color);
        padding: ${LAYOUT.desktop.paddingVertical} ${LAYOUT.desktop.paddingHorizontal};
      `}
  }
`;

// CrmX Admin Header Styles
export const CrmXHeaderContainer = styled.div`
  background: transparent; /* Убираем градиент из заголовка */
  padding: 8px 0; /* Уменьшаем отступы */
  display: flex;
  align-items: center;
  position: relative;
  z-index: 10; /* Поднимаем над градиентом и SVG */
`;

export const CrmXHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0 16px; /* Уменьшаем боковые отступы */
`;

export const CrmXLogoSection = styled.div`
  display: flex;
  align-items: center;
  min-width: 200px;
`;

export const CrmXLogoText = styled.h1`
  margin: 0;
  font-size: 18px; /* Уменьшаем размер шрифта */
  font-weight: 600; /* Немного уменьшаем жирность */
  color: #ffffff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
`;

export const CrmXSearchSection = styled.div`
  flex: 1;
  max-width: 300px; /* Уменьшаем максимальную ширину */
  margin: 0 20px; /* Уменьшаем отступы */
`;

export const CrmXIconsSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 200px;
  justify-content: flex-end;
`;

export const CrmXIconButton = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #ffffff;
  font-size: 14px;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateY(-1px);
  }
`;

interface AuthButtonProps {
  $isAuthenticated?: boolean;
}

export const AuthButton = styled.button<AuthButtonProps>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  background: ${(props) => props.$isAuthenticated 
    ? 'rgba(255, 255, 255, 0.15)' 
    : 'rgba(255, 255, 255, 0.95)'};
  color: ${(props) => props.$isAuthenticated 
    ? '#ffffff' 
    : '#1677ff'};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${(props) => props.$isAuthenticated 
      ? 'rgba(255, 255, 255, 0.25)' 
      : '#ffffff'};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  span {
    @media (max-width: 480px) {
      display: none;
    }
  }
`;

// Mobile Auth Button
export const MobileAuthButton = styled.button<AuthButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 8px;
  border: none;
  background: ${(props) => props.$isAuthenticated 
    ? 'rgba(255, 77, 79, 0.1)' 
    : '#1677ff'};
  color: ${(props) => props.$isAuthenticated 
    ? '#ff4d4f' 
    : '#ffffff'};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: 0.9;
  }
`;

// Mobile Header Centered - only Teacher's Space text
export const MobileHeaderCentered = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 8px 16px;
`;

// Mobile Header Row - with SignIn on right
export const MobileHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 16px;
`;

// Mobile SignIn Button - white background like desktop, smaller size
export const MobileSignInButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 5px 10px;
  border-radius: 6px;
  border: none;
  background: rgba(255, 255, 255, 0.95);
  color: #1677ff;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background: #ffffff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  .anticon {
    font-size: 12px;
  }
`;